// ResetPasswordPage — the auth screen at /reset-password?token=...
//
// Reads the `?token=` query param from the reset email, lets the user
// set a new password + confirmation, and posts to PocketBase's
// `users.confirmPasswordReset` endpoint. On success the user lands on
// a confirmation screen with a link back to /login (they are NOT
// auto-signed-in: that would require a second password round-trip and
// it does not match what the rest of the app expects — once a password
// is changed on the server, the next /login picks up the new
// credential).
//
// The PocketBase default email template (v0.23+) builds the reset
// URL as `<meta.appURL>/_/#/confirm-password-reset/<RESET_TOKEN>`.
// With `meta.appURL` set to the app origin (per orchestrator's 16:05
// finding for R-11) and `/_/` blocked at the edge, the email link is
// unreachable. The orchestrator must update the live email template
// to point at our `/reset-password?token=<RESET_TOKEN>` route — see
// the run-log note. The token parameter is the same JWT-shaped string
// the server hands out; this page only reads it and forwards it.
//
// The page is also reachable under `/:locale/reset-password` via the
// App.tsx wiring so a shared `de/...` link renders in German.

import { useMemo, useState } from "react";
import type { FormEvent, ReactElement } from "react";
import { Link, Navigate, useSearchParams } from "react-router-dom";
import { useI18n } from "../../i18n";
import { useAuth } from "../../services/auth";
import { AuthError, AuthErrorCode } from "../../services/authErrors";

export function ResetPasswordPage(): ReactElement {
  const { t } = useI18n();
  const { confirmPasswordReset, user } = useAuth();
  const [params] = useSearchParams();
  // Memoize the token extraction so the page is stable across renders
  // — a fresh URLSearchParams object on every render would otherwise
  // force us to re-derive on every keystroke.
  const token = useMemo(() => params.get("token") ?? "", [params]);
  const [password, setPassword] = useState("");
  const [passwordConfirm, setPasswordConfirm] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  // A failed server confirm puts us in the "the link is bad" terminal
  // state: we render the "request a new one" message and a link to
  // the forgot-password screen. The user can navigate away but cannot
  // retry from this state without a fresh token.
  const [invalidToken, setInvalidToken] = useState(false);
  const [saved, setSaved] = useState(false);
  const online = typeof navigator === "undefined" ? true : navigator.onLine;

  if (user) {
    return <Navigate to="/app/workout" replace />;
  }

  if (!token) {
    // No token at all — treat as invalid-link immediately rather than
    // rendering a form that cannot succeed.
    return (
      <ResetShell t={t}>
        <p
          data-testid="reset-password-invalid-token"
          role="alert"
          className="font-sans text-base"
          style={{ color: "var(--color-blood)" }}
        >
          {t("auth.resetPassword.invalidToken")}
        </p>
        <Link
          to="/forgot-password"
          data-testid="reset-password-request-new"
          className="font-display uppercase tracking-[0.02em] text-[var(--color-void)]"
          style={primaryLinkStyle}
        >
          {t("auth.forgotPassword.title")}
        </Link>
      </ResetShell>
    );
  }

  if (saved) {
    return (
      <ResetShell t={t}>
        <p
          data-testid="reset-password-success"
          className="font-sans text-base text-[var(--color-ink-hi)]"
        >
          {t("auth.resetPassword.success")}
        </p>
        <Link
          to="/login"
          data-testid="reset-password-sign-in"
          className="font-display uppercase tracking-[0.02em] text-[var(--color-void)]"
          style={primaryLinkStyle}
        >
          {t("auth.resetPassword.signIn")}
        </Link>
      </ResetShell>
    );
  }

  if (invalidToken) {
    return (
      <ResetShell t={t}>
        <p
          data-testid="reset-password-invalid-token"
          role="alert"
          className="font-sans text-base"
          style={{ color: "var(--color-blood)" }}
        >
          {t("auth.resetPassword.invalidToken")}
        </p>
        <Link
          to="/forgot-password"
          data-testid="reset-password-request-new"
          className="font-display uppercase tracking-[0.02em] text-[var(--color-void)]"
          style={primaryLinkStyle}
        >
          {t("auth.forgotPassword.title")}
        </Link>
      </ResetShell>
    );
  }

  const onSubmit = async (e: FormEvent) => {
    e.preventDefault();
    if (!online) return;
    setSubmitError(null);
    // Client-side password-mismatch check. We surface the same generic
    // error string the user would see for any server-side validation
    // failure, so we never tell them "the passwords are different" in
    // a way that would help an attacker probing token validity.
    if (password !== passwordConfirm) {
      setSubmitError(t("auth.resetPassword.error"));
      return;
    }
    if (password.length === 0) {
      setSubmitError(t("auth.resetPassword.error"));
      return;
    }
    setSubmitting(true);
    try {
      await confirmPasswordReset(token, password, passwordConfirm);
      setSaved(true);
    } catch (err) {
      if (err instanceof AuthError) {
        if (err.code === AuthErrorCode.InvalidToken) {
          setInvalidToken(true);
        } else if (err.code === AuthErrorCode.Offline) {
          setSubmitError(t("auth.resetPassword.offline"));
        } else {
          setSubmitError(t("auth.resetPassword.error"));
        }
      } else {
        setSubmitError(t("auth.resetPassword.error"));
      }
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <main
      data-testid="reset-password-page"
      className="mx-auto flex min-h-screen w-full max-w-[420px] flex-col gap-6 px-6 py-12"
    >
      <header>
        <p className="font-subhead text-sm uppercase tracking-[0.06em] text-[var(--color-lime)]">
          {t("brand.name")}
        </p>
        <h1 className="font-display mt-2 text-4xl uppercase leading-[1.1] tracking-[0.02em] text-[var(--color-ink-hi)]">
          {t("auth.resetPassword.title")}
        </h1>
        <p className="font-sans mt-4 text-sm text-[var(--color-ink-mute)]">
          {t("auth.resetPassword.intro")}
        </p>
      </header>
      <form
        onSubmit={onSubmit}
        className="flex flex-col gap-4"
        data-testid="reset-password-form"
        aria-disabled={!online}
      >
        <label className="flex flex-col gap-1">
          <span className="font-subhead text-xs uppercase tracking-[0.06em] text-[var(--color-ink-mute)]">
            {t("auth.resetPassword.newPassword")}
          </span>
          <input
            type="password"
            required
            autoComplete="new-password"
            value={password}
            onChange={(e) => setPassword(e.currentTarget.value)}
            disabled={!online || submitting}
            data-testid="reset-password-new-input"
            className="border border-[var(--color-line-strong)] bg-[var(--color-slab)] px-3 text-[var(--color-ink-hi)]"
            style={{
              minHeight: "64px",
              borderRadius: "var(--radius-sharp)",
              fontSize: 18,
            }}
          />
        </label>
        <label className="flex flex-col gap-1">
          <span className="font-subhead text-xs uppercase tracking-[0.06em] text-[var(--color-ink-mute)]">
            {t("auth.resetPassword.confirmPassword")}
          </span>
          <input
            type="password"
            required
            autoComplete="new-password"
            value={passwordConfirm}
            onChange={(e) => setPasswordConfirm(e.currentTarget.value)}
            disabled={!online || submitting}
            data-testid="reset-password-confirm-input"
            className="border border-[var(--color-line-strong)] bg-[var(--color-slab)] px-3 text-[var(--color-ink-hi)]"
            style={{
              minHeight: "64px",
              borderRadius: "var(--radius-sharp)",
              fontSize: 18,
            }}
          />
        </label>
        {!online && (
          <p
            data-testid="reset-password-offline"
            className="font-sans text-sm"
            style={{ color: "var(--color-ink-mute)" }}
          >
            {t("auth.resetPassword.offline")}
          </p>
        )}
        {submitError && (
          <p
            data-testid="reset-password-error"
            role="alert"
            className="font-sans text-sm"
            style={{ color: "var(--color-blood)" }}
          >
            {submitError}
          </p>
        )}
        <button
          type="submit"
          disabled={!online || submitting}
          data-testid="reset-password-submit"
          className="font-display uppercase tracking-[0.02em] text-[var(--color-void)]"
          style={{
            minHeight: "64px",
            fontSize: 22,
            backgroundColor: "var(--color-lime)",
            borderRadius: "var(--radius-sharp)",
            opacity: !online || submitting ? 0.6 : 1,
          }}
        >
          {submitting ? t("auth.resetPassword.saving") : t("auth.resetPassword.submit")}
        </button>
      </form>
      <p className="font-sans text-sm text-[var(--color-ink-mute)]">
        <Link to="/login" className="text-[var(--color-lime)] underline-offset-2 hover:underline">
          {t("auth.forgotPassword.backToLogin")}
        </Link>
      </p>
    </main>
  );
}

const primaryLinkStyle: React.CSSProperties = {
  minHeight: "64px",
  fontSize: 22,
  backgroundColor: "var(--color-lime)",
  borderRadius: "var(--radius-sharp)",
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
  textDecoration: "none",
};

function ResetShell({
  t,
  children,
}: {
  t: (key: string) => string;
  children: React.ReactNode;
}): ReactElement {
  return (
    <main
      data-testid="reset-password-page"
      className="mx-auto flex min-h-screen w-full max-w-[420px] flex-col gap-6 px-6 py-12"
    >
      <header>
        <p className="font-subhead text-sm uppercase tracking-[0.06em] text-[var(--color-lime)]">
          {t("brand.name")}
        </p>
        <h1 className="font-display mt-2 text-4xl uppercase leading-[1.1] tracking-[0.02em] text-[var(--color-ink-hi)]">
          {t("auth.resetPassword.title")}
        </h1>
      </header>
      {children}
    </main>
  );
}
